import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaEye, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";
import { saveNegotiationDraft } from "../../../../services/Templates";
import DocxEditorCom from "../../../../components/DocxEditorCom";
import TemplatePreviewModal from "../components/TemplatePreviewModal";

const NegotiationStep4 = ({
  formObj = {},
  setFormObj,
  setLoading: parentSetLoading,
  nextStep,
  prevStep,
}) => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  const negotiationId = formObj?.negotiation_id || formObj?.id;

  // UI state
  const [content, setContent] = useState("");
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);

  // Safe setter helper
  const safeSet = (setter) => (value) => {
    if (mountedRef.current) setter(value);
  };
  const safeSetContent = safeSet(setContent);
  const safeSetSaving = safeSet(setIsSaving);
  const safeSetEditorReady = safeSet(setIsEditorReady);
  const safeSetError = safeSet(setError);

  // Escape regex special chars in placeholder keys
  const escapeRegex = (str = "") =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Replace placeholders
  const replacePlaceholders = useCallback((htmlContent = "", questionnaireData = {}) => {
    if (!htmlContent) return "";
    if (!questionnaireData || Object.keys(questionnaireData).length === 0) return htmlContent;

    let updated = htmlContent;
    Object.keys(questionnaireData).forEach((rawKey) => {
      const key = String(rawKey);
      const value = questionnaireData[rawKey] ?? "";
      const pattern = new RegExp(`{{\\s*${escapeRegex(key)}\\s*}}`, "g");
      updated = updated.replace(pattern, value);
    });

    return updated;
  }, []);

  // Build initial content
  const getInitialContent = useCallback(() => {
    try {
      const apiHtml = (formObj?.html_content ?? "").toString();
      const localEdited = (formObj?.edited_content ?? formObj?.draft_content ?? "").toString();
      const template =
        (formObj?.template_content ??
          (typeof window !== "undefined" && sessionStorage.getItem("template_html_content")) ??
          "")?.toString() ?? "";

      let questionnaireData = formObj?.questionnaire_data ?? {};
      if (!questionnaireData || Object.keys(questionnaireData).length === 0) {
        try {
          const saved = typeof window !== "undefined" && sessionStorage.getItem("step3_formData");
          if (saved) questionnaireData = JSON.parse(saved);
        } catch (err) {
          questionnaireData = {};
        }
      }

      const baseHtml =
        (localEdited && localEdited.trim()) ||
        (apiHtml && apiHtml.trim()) ||
        (template && template.trim()) ||
        "";
      const final = replacePlaceholders(baseHtml, questionnaireData);
      return final || "<p></p>";
    } catch (err) {
      console.error("Error building initial content:", err);
      return "<p></p>";
    }
  }, [formObj, replacePlaceholders]);

  // Initialize editor content
  useEffect(() => {
    mountedRef.current = true;
    safeSetEditorReady(false);
    const initial = getInitialContent();
    safeSetContent(initial);
    safeSetEditorReady(true);
    safeSetError(null);

    return () => {
      mountedRef.current = false;
    };
  }, [getInitialContent, formObj?.template_uuid]);

  // Persist to parent
  const persistToParent = useCallback(
    (payloadUpdates = {}) => {
      if (typeof setFormObj === "function") {
        setFormObj((prev = {}) => ({ ...(prev || {}), ...payloadUpdates }));
      }
    },
    [setFormObj]
  );

  // Save draft
  const handleSaveDraft = useCallback(async () => {
    if (!content?.trim()) return toast.warning("Content cannot be empty");
    if (!negotiationId) return toast.error("Negotiation ID missing");

    safeSetSaving(true);
    parentSetLoading && parentSetLoading(true);

    try {
      await saveNegotiationDraft({ negotiation_id: negotiationId, html_content: content });
      persistToParent({
        html_content: content,
        draft_content: content,
        last_saved: new Date().toISOString(),
      });
      sessionStorage.setItem("draft_content", content);
      toast.success("Draft saved successfully");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Error saving draft";
      toast.error(msg);
      safeSetError(msg);
    } finally {
      safeSetSaving(false);
      parentSetLoading && parentSetLoading(false);
    }
  }, [content, negotiationId, parentSetLoading, persistToParent]);

  // Save and Next
  const handleNext = useCallback(async () => {
    if (!content?.trim()) return toast.error("Content cannot be empty");
    if (!negotiationId) return toast.error("Negotiation ID missing");

    parentSetLoading && parentSetLoading(true);
    safeSetSaving(true);

    try {
      await saveNegotiationDraft({ negotiation_id: negotiationId, html_content: content });
      persistToParent({ html_content: content, final_content: content });
      sessionStorage.setItem("final_content", content);
      toast.success("Document saved successfully");

      if (nextStep) nextStep();
      else if (uuid) navigate(`/client-portal/negotiation-contract/create/${uuid}/5`);
      else navigate("/client-portal/negotiation-contract");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Error saving document";
      toast.error(msg);
      safeSetError(msg);
    } finally {
      parentSetLoading && parentSetLoading(false);
      safeSetSaving(false);
    }
  }, [content, negotiationId, nextStep, uuid, navigate, parentSetLoading, persistToParent]);

  const handleBack = useCallback(() => {
    persistToParent({ edited_content: content });
    if (prevStep) prevStep();
    else navigate(-1);
  }, [content, persistToParent, prevStep, navigate]);

  // Open Preview Modal
  const handlePreviewOpen = useCallback(() => {
    if (!content?.trim()) return toast.warning("Nothing to preview");
    setPreviewData({ html_content: content, title: "Negotiation Preview" });
    setShowPreviewModal(true);
  }, [content]);

  return (
    <div className="negotiation-container negotiation-step4">
        <div className="negotiation-step1-header">
        <div className="negotiation-header-left">
          <h2 className="step1-title">Edit Contract</h2>
          <p className="step1-subtitle">Review and modify the negotiation text below.</p>
        </div>
        <div className="negotiation-header-right">
          <span className="step-indicator">Step 4 of 6</span>
        </div>
      </div>

      <div className="negotiation-wrapper">
        <div className="row">
          {/* <div className="col-3 bg-light">

          </div> */}
          <div className="col-12">
            <div className="negotiation-card step4-editor-card">
              <main className="sleeve-main-card">
                <DocxEditorCom
                  htmlContent={content}
                  onContentChange={(updatedHtml) => safeSetContent(updatedHtml)}
                />

                <div className="sleeve-step-buttons mt-0">
                  <button
                    onClick={handleBack}
                    disabled={!isEditorReady || isSaving}
                    className="sleeve-btn-back"
                  >
                    Back
                  </button>

                  <div className="sleeve-step-buttons-right">
                    <button
                      onClick={handlePreviewOpen}
                      className="sleeve-btn-save"
                      disabled={!isEditorReady || isSaving}
                    >
                      <FaEye /> Preview
                    </button>

                    <button
                      onClick={handleSaveDraft}
                      className="sleeve-btn-save"
                      disabled={!isEditorReady || isSaving}
                    >
                      <FaSave /> Save & Exit
                    </button>

                    <button
                      className="sleeve-btn-next"
                      onClick={handleNext}
                      disabled={!isEditorReady || isSaving}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </main>
            </div>
          </div>

        </div>

      </div>

      {/* Preview Modal */}
      <TemplatePreviewModal
        show={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        template={previewData}
      />
    </div>
  );
};

export default NegotiationStep4;
